/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { environment } from "../../../environments/environment";
import { Message } from "CalltakingCoreApi";
import { BannerLanguageInputs, ManualLanguageOverride, MessageTranslationLabels,
    TranslationIndicatorMode, TranslationLanguage, TranslationStateEntry} from "../model/translation.model";
import { LanguageChangeSource, LanguageOverride } from "../model/language.model";

export function deriveLanguageChangeSource(messageSenderType: 'AGENT' | 'EXTERNAL' | string): LanguageChangeSource {
    return messageSenderType === 'AGENT' ? LanguageChangeSource.Manual : LanguageChangeSource.Automatic;
}

export function isLanguageExplicitlySetToNonDefault(lang: string | null | undefined): boolean {
    return Boolean(lang) && !isSameLanguage(lang, environment.defaultLanguage);
}

export function isLanguageEqualsDefault(lang: string | null | undefined): boolean {
    return isSameLanguage(lang, environment.defaultLanguage);
}

export function isSameLanguage(a, b): boolean {
    if (!a || !b) return false;
    try {
        return new Intl.Locale(a).language === new Intl.Locale(b).language;
    } catch {
        return a.toLowerCase() === b.toLowerCase();
    }
}

export function getLanguageDisplayName(
    languageCode: string,
    locale: string = environment.defaultLanguage
): string {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    return displayNames.of(languageCode) ?? languageCode;
}

export function hasTranslation(message: Message): boolean {
    return Boolean(message?.sourceText || message?.targetText);
}

export function getMessageTranslationLabels(message: Message): MessageTranslationLabels {
    const isTranslated = hasTranslation(message);
    if (!isTranslated) {
        return { isTranslated: false, topCode: undefined, bottomCode: undefined, kind: undefined, foreignLanguageCode: undefined };
    }

    const isAgent = message.messageSenderType === 'AGENT';
    const hasSourceText = Boolean(message.sourceText);

    const isSelfLanguageLogged = isAgent
        && !hasSourceText
        && Boolean(message.targetText)
        && isLanguageEqualsDefault(message.targetLanguage);

    if (isSelfLanguageLogged) {
        const foreignLanguageCode = message.messageLanguage;
        return {
            isTranslated: true,
            topCode: foreignLanguageCode,
            bottomCode: undefined,
            kind: TranslationIndicatorMode.SentSelfLanguageLogged,
            foreignLanguageCode
        };
    }

    const topCode = hasSourceText ? message.sourceLanguage : message.messageLanguage;
    const bottomCode = hasSourceText ? message.messageLanguage : message.targetLanguage;
    const kind: TranslationIndicatorMode = isAgent ? TranslationIndicatorMode.SentTranslated : TranslationIndicatorMode.ReceivedTranslated;
    const foreignLanguageCode = kind === TranslationIndicatorMode.ReceivedTranslated ? topCode : bottomCode;

    return { isTranslated: true, topCode, bottomCode, kind, foreignLanguageCode };
}

export function computeLanguageChangeTimeline(messages: Message[]): TranslationStateEntry[] {
    const entries: TranslationStateEntry[] = [];
    let lastLangKey: string | null = null;

    const sorted = [...(messages || [])].sort(
        (a, b) => new Date(a.createdTimestamp as any).getTime() - new Date(b.createdTimestamp as any).getTime()
    );

    for (const msg of sorted) {
        const foreignLang = msg.messageLanguage;
        if (!foreignLang) continue;

        const isDefaultLanguage = isLanguageEqualsDefault(foreignLang);
        const isAgent = msg.messageSenderType === 'AGENT';

        if (isDefaultLanguage ? !isAgent : !hasTranslation(msg)) continue;
        if (foreignLang === lastLangKey) continue;

        const changeSource = deriveLanguageChangeSource(isAgent ? 'AGENT' : 'EXTERNAL');
        const timestamp = new Date(msg.createdTimestamp as any).getTime();
        const entry: TranslationStateEntry = { languageCode: foreignLang, changeSource, originMessageUuid: msg.uuid, timestamp, isFirst: lastLangKey === null };

        entries.push(entry);
        lastLangKey = foreignLang;
    }

    return entries;
}

export function mapCodesToTranslationLanguages(codes: string[], available: TranslationLanguage[]): TranslationLanguage[] {
    const unique = codes.filter((v, i, a) => a.indexOf(v) === i);
    return unique.map((code) => available
        .find((l) => l.languageCode === code) ?? { languageCode: code, name: code });
}

export function createManualLanguageOverride(
    languageCode: string,
    timestamp: number
): ManualLanguageOverride {
    return { languageCode, timestamp };
}

export function isManualOverrideStillValid(
    override: ManualLanguageOverride | null,
    latestEntry: TranslationStateEntry | null
): boolean {
    if (!override) return false;
    return !latestEntry || latestEntry.timestamp <= override.timestamp;
}

export function resolveBannerLanguage(inputs: BannerLanguageInputs): LanguageOverride | null {
    if (inputs.hasActiveTranslationError) {
        if (inputs.previousEntry) {
            return { languageCode: inputs.previousEntry.languageCode, languageChangeSource: inputs.previousEntry.changeSource };
        }
    } else {
        if (isManualOverrideStillValid(inputs.manualLanguageOverride, inputs.latestEntry)) {
            return { languageCode: inputs.manualLanguageOverride!.languageCode, languageChangeSource: LanguageChangeSource.Manual };
        }
        if (inputs.latestEntry) {
            return { languageCode: inputs.latestEntry.languageCode, languageChangeSource: inputs.latestEntry.changeSource };
        }
    }
    if (inputs.restoredLanguage && inputs.restoredLanguage !== environment.defaultLanguage) {
        return { languageCode: inputs.restoredLanguage, languageChangeSource: inputs.restoredLanguageChangeSource ?? LanguageChangeSource.Manual };
    }
    if (inputs.fallbackLanguage && inputs.fallbackLanguage !== environment.defaultLanguage) {
        return { languageCode: inputs.fallbackLanguage, languageChangeSource: LanguageChangeSource.Automatic };
    }
    return null;
}
